import type { CrossLink, Edges, NodeMap, TagEdges, TagMap } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * The map's content: nodes, the inverted parent index, tags, tag parents and cross-links.
 *
 * THE CONTAINER-IDENTITY CONTRACT LIVES HERE, and it is the one rule in this file that is
 * not obvious. The vanilla script mutates `nodes[id]`, `tags[id]` and friends IN PLACE, so a
 * node object's identity says nothing about whether its label changed. What carries "something
 * changed" is the identity of the CONTAINER, which the bridge replaces on every sync
 * (`{...nodes}`) precisely so that it does.
 *
 * So `replaceGraph` ADOPTS whatever containers it is handed, without comparing them. A shallow
 * equality check here would be actively wrong: the elements are the same references even when
 * their contents differ, so it would report "unchanged" after an in-place label edit and every
 * `useMemo` keyed on `nodes` would go permanent. That bug has already happened once in this
 * migration — see `app/state.ts`'s note on `AppSnapshot`, and `lib/graph.ts`'s
 * `buildGraph()`, which caches on the identity of `edges`.
 *
 * `positions` is the deliberate exception, and it can be, because it extracts primitives —
 * see that slice.
 */
export interface GraphSlice {
  nodes: NodeMap;
  edges: Edges;
  tags: TagMap;
  tagEdges: TagEdges;
  links: readonly CrossLink[];
  /**
   * Is this graph a SUBSET of the server's — were nodes withheld from the payload it came
   * from (V0.43)?
   *
   * It rides with the five containers because it arrives with them, from the same fetch, and
   * changes only when they do. `commitPositions` is the reason it has to be here rather than
   * in `app/state.ts` alone: it runs outside React, reads this store, and must not publish
   * canonical positions derived from a partial tree.
   */
  mapFiltered: boolean;

  /**
   * Take a whole new graph.
   *
   * One action rather than five, because these five always change together: a reparent moves
   * `edges` and the node's stored depth, a tag rename touches `tags`, and a load replaces all
   * of them. Splitting it would invite a caller to set three of the five and leave the index
   * in `lib/graph.ts` describing a graph that no longer exists.
   */
  replaceGraph: (next: {
    nodes: NodeMap;
    edges: Edges;
    tags: TagMap;
    tagEdges: TagEdges;
    links: readonly CrossLink[];
    mapFiltered: boolean;
  }) => void;
}

export const createGraphSlice: SliceCreator<GraphSlice> = (set) => ({
  nodes: {},
  edges: {},
  tags: {},
  tagEdges: {},
  links: [],
  mapFiltered: false,

  replaceGraph: (next) =>
    set({
      nodes: next.nodes,
      edges: next.edges,
      tags: next.tags,
      tagEdges: next.tagEdges,
      links: next.links,
      mapFiltered: next.mapFiltered,
    }),
});
